"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.UserService = exports.NoConsentError = exports.PendingUserActionError = void 0;
// eslint-disable-next-line max-classes-per-file
const isEqual_1 = __importDefault(require("lodash/isEqual"));
const superstruct_1 = require("superstruct");
const p_queue_1 = __importDefault(require("p-queue"));
const Common_1 = require("./Common");
const Logger_1 = __importDefault(require("./Logger"));
const Utils_1 = require("./Utils");
const UserConsentTypes_1 = require("./types/UserConsentTypes");
const JsonWebTokenTypes_1 = require("./types/JsonWebTokenTypes");
class PendingUserActionError extends Error {
    constructor(message) {
        super(message !== null && message !== void 0 ? message : "Pending user action for user consent preferences");
    }
}
exports.PendingUserActionError = PendingUserActionError;
class NoConsentError extends Error {
    constructor() {
        super("There is no User Consent");
    }
}
exports.NoConsentError = NoConsentError;
class UserService {
    constructor(contextService, localDataService, restService, cookieService) {
        this.contextService = contextService;
        this.localDataService = localDataService;
        this.restService = restService;
        this.cookieService = cookieService;
        this.pQueue = new p_queue_1.default({ concurrency: 1 });
        this.onUserChanging = null;
        this.saveUserConsentDetectToken = this.saveUserConsentDetectToken.bind(this);
    }
    get unnamedUser() {
        const cookieUnnamedUserId = this.cookieService.unnamedUserId;
        if (cookieUnnamedUserId) {
            return {
                id: cookieUnnamedUserId,
                provider: Common_1.USER_IDENTITY_PROVIDER.COOKIE,
            };
        }
        return {
            id: this.localDataService.unnamedUserId,
            provider: Common_1.USER_IDENTITY_PROVIDER.SDK,
        };
    }
    clear() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.pQueue.add(() => __awaiter(this, void 0, void 0, function* () {
                this.localDataService.isNamedUser = false;
                this.contextService.context.user = undefined;
                this.contextService.context.hubId = undefined;
                this.contextService.context.hubUrn = undefined;
                this.contextService.context.projectId = undefined;
                this.contextService.context.projectUrn = undefined;
                this.contextService.context.userIntent = undefined;
                this.contextService.context.facets.user = undefined;
            }));
        });
    }
    setup(identify) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.pQueue.add(() => __awaiter(this, void 0, void 0, function* () {
                try {
                    if (this.contextService.context.authProvider || identify) {
                        yield this.identifyUser();
                    }
                    else {
                        yield this.restoreUser();
                    }
                }
                catch (e) {
                    Logger_1.default.error("ERROR Setup User", e);
                }
                finally {
                    Logger_1.default.debug("END Setup User", this.contextService.context);
                }
            }));
        });
    }
    restoreUser() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            Logger_1.default.debug("Restoring user");
            if (this.localDataService.isNamedUser) {
                const userConsent = (_a = (yield this.setupNamedUserDetectToken())) !== null && _a !== void 0 ? _a : (yield this.setupLocalUnnamedUser());
                if (userConsent == null) {
                    Logger_1.default.debug("There is no cached User Consent to restore");
                }
                else {
                    this.reactToUserConsent(userConsent);
                }
            }
            else {
                const userConsent = yield this.setupLocalUnnamedUser();
                if (userConsent != null) {
                    this.reactToUserConsent(userConsent);
                }
            }
        });
    }
    identifyUser() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            Logger_1.default.debug("Identify User");
            const userConsent = (_b = (_a = (yield this.setupNamedUserDetectToken())) !== null && _a !== void 0 ? _a : (yield this.setupLocalUnnamedUser())) !== null && _b !== void 0 ? _b : (yield this.setupRemoteUnnamedUser());
            this.reactToUserConsent(userConsent);
        });
    }
    reactToUserConsent(userConsent) {
        var _a, _b;
        Logger_1.default.debug("React to User Consent", userConsent);
        this.contextService.context.userConsentCookiePreferences = this.detectCookiePrivacySetting(userConsent.consentCopy);
        this.contextService.writeUserConsentPreferences(userConsent.preferences);
        this.contextService.writeUserConsentCopy(userConsent.consentCopy);
        const useCallbackFlow = this.contextService.context.tokenInfo == null && !this.contextService.context.authProvider;
        if (useCallbackFlow && UserService.shouldShowUserConsentDialog(userConsent)) {
            (_b = (_a = this.contextService.context.callback).buildUserConsentDialog) === null || _b === void 0 ? void 0 : _b.call(_a, userConsent.preferences, userConsent.consentCopy, this.saveUserConsentDetectToken);
        }
        this.contextService.dispatch("user_consent");
    }
    /**
     * Attempts to fetch the access token automatically, in order of preferences
     * 1) Auth provider (currently only CFP if provided)
     * 2) From Context, eg via loadUserConsentPreferences
     * 3) From getUserToken callback
     * @returns Promise that resolves the parsed token information
     */
    detectToken() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.contextService.context.authProvider) {
                Logger_1.default.debug("Retrieve token from cfp");
                const cfpToken = yield this.contextService.context.authProvider.getAccessToken();
                const cfpTokenInfo = cfpToken && (yield this.parseTokenInfo(cfpToken));
                if (cfpTokenInfo && cfpTokenInfo instanceof Error) {
                    Logger_1.default.debug("Unable to parse CFP access token");
                    return undefined;
                }
                this.contextService.context.tokenInfo = cfpTokenInfo || undefined;
                return this.contextService.context.tokenInfo;
            }
            if (this.contextService.context.tokenInfo != null) {
                return this.contextService.context.tokenInfo;
            }
            try {
                const token = yield ((_b = (_a = this.contextService.context.callback).retrieveUserToken) === null || _b === void 0 ? void 0 : _b.call(_a));
                const tokenInfo = yield this.parseTokenInfo(token);
                if (tokenInfo == null) {
                    Logger_1.default.debug("There is no token provider or its token");
                    return undefined;
                }
                if (tokenInfo instanceof Error) {
                    Logger_1.default.error(tokenInfo.message, tokenInfo);
                    return undefined;
                }
                return tokenInfo;
            }
            catch (e) {
                Logger_1.default.error("Error invoking user token provider", e);
                return undefined;
            }
        });
    }
    setupNamedUserDetectToken() {
        return __awaiter(this, void 0, void 0, function* () {
            const tokenInfo = yield this.detectToken();
            return tokenInfo != null ? this.setupNamedUser(tokenInfo) : undefined;
        });
    }
    setupNamedUser(tokenInfo) {
        return __awaiter(this, void 0, void 0, function* () {
            Logger_1.default.debug("Trying Setup Named User");
            this.contextService.context.user = {
                provider_name: tokenInfo.provider,
                user_id: tokenInfo.userId,
                logged_in: true,
            };
            const { id: unnamedUserId } = this.unnamedUser;
            if (unnamedUserId) {
                this.contextService.context.facets.alias = {
                    new_id: tokenInfo.userId,
                    previous_id: unnamedUserId,
                };
            }
            this.localDataService.isNamedUser = true;
            switch (tokenInfo.provider) {
                case Common_1.USER_IDENTITY_PROVIDER.LDAP:
                    this.contextService.context.user.ldap_root = tokenInfo.issuer;
                    break;
                case Common_1.USER_IDENTITY_PROVIDER.O2:
                default:
                    return this.restService.doGetNamedUserConsent(tokenInfo).then(({ data: userConsent }) => __awaiter(this, void 0, void 0, function* () {
                        Logger_1.default.debug("Done Setup Named User", userConsent);
                        return userConsent;
                    }));
            }
            const userConsent = (yield this.restService.doGetUnnamedUserConsent()).data;
            const now = Date.now();
            // If named user is not from O2, they are internal users, and we will simply set all preferences to true
            const preferences = userConsent.consentCopy.copies.map(({ consentId }) => ({
                consentId,
                lastUpdated: now,
                optIn: true,
            }));
            // Reconstruct the user consent preferences data
            return Object.assign(Object.assign({}, userConsent), { preferences, userActionRequired: false, userId: tokenInfo.userId });
        });
    }
    setupLocalUnnamedUser() {
        return __awaiter(this, void 0, void 0, function* () {
            Logger_1.default.debug("Trying Setup Local Unnamed User");
            const { id: unnamedUserId, provider } = this.unnamedUser;
            const preferences = this.localDataService.userConsentPreferences;
            const consentCopy = this.localDataService.userConsentCopy;
            if (!unnamedUserId || !preferences || !consentCopy) {
                Logger_1.default.debug("There is no cached Unnamed User");
                return undefined;
            }
            this.contextService.context.user = {
                provider_name: provider,
                user_id: unnamedUserId,
                logged_in: false,
            };
            const userConsent = {
                userId: unnamedUserId,
                userActionRequired: false,
                preferences,
                consentCopy,
            };
            this.localDataService.isNamedUser = false;
            Logger_1.default.debug("Done Setup Local Unnamed User", userConsent);
            return userConsent;
        });
    }
    setupRemoteUnnamedUser() {
        return __awaiter(this, void 0, void 0, function* () {
            Logger_1.default.debug("Trying Setup Remote Unnamed User");
            const consentCopy = yield this.getUserConsentCopy();
            const sdkUserId = (0, Utils_1.genGuid)();
            const cookieUserId = this.cookieService.unnamedUserId;
            this.contextService.context.user = cookieUserId
                ? {
                    provider_name: Common_1.USER_IDENTITY_PROVIDER.COOKIE,
                    user_id: cookieUserId,
                    logged_in: false,
                }
                : {
                    provider_name: Common_1.USER_IDENTITY_PROVIDER.SDK,
                    user_id: sdkUserId,
                    logged_in: false,
                };
            const userConsent = {
                userId: this.contextService.context.user.user_id,
                userActionRequired: false,
                preferences: UserService.userConsentCopyToPreferences(consentCopy),
                consentCopy,
            };
            this.localDataService.unnamedUserId = sdkUserId;
            this.localDataService.userConsentCopy = userConsent.consentCopy;
            this.localDataService.userConsentPreferences = userConsent.preferences;
            this.localDataService.isNamedUser = false;
            Logger_1.default.debug("Done Setup Remote Unnamed User", userConsent);
            return userConsent;
        });
    }
    parseTokenInfo(userToken) {
        return __awaiter(this, void 0, void 0, function* () {
            const token = (0, Utils_1.stripAccessTokenType)(userToken);
            if (token == null) {
                return undefined;
            }
            Logger_1.default.debug("Parsing user token, please wait...");
            try {
                const tokenParts = token.split(".");
                const decodedInfo = atob(tokenParts[1]);
                const parsedInfo = JSON.parse(decodedInfo);
                Logger_1.default.debug("Parsed user token", parsedInfo);
                if ((0, superstruct_1.is)(parsedInfo, JsonWebTokenTypes_1.AdskToken)) {
                    return {
                        userId: parsedInfo.userid,
                        userToken: token,
                        provider: Common_1.USER_IDENTITY_PROVIDER.O2,
                        issuer: parsedInfo.iss,
                    };
                }
                if ((0, superstruct_1.is)(parsedInfo, JsonWebTokenTypes_1.WindowsToken)) {
                    return {
                        userId: parsedInfo.email,
                        userToken: token,
                        provider: Common_1.USER_IDENTITY_PROVIDER.LDAP,
                        issuer: parsedInfo.iss,
                    };
                }
                Logger_1.default.error("Unexpected payload");
                return new Error(Common_1.ERROR_MESSAGE.invalidUserToken);
            }
            catch (e) {
                Logger_1.default.error("Error while parsing the user token", e);
                return new Error(Common_1.ERROR_MESSAGE.invalidUserToken);
            }
        });
    }
    getCurrentUserConsentPreferences() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.pQueue.add(() => __awaiter(this, void 0, void 0, function* () { var _a; return (_a = this.contextService.context.userConsentCookiePreferences) !== null && _a !== void 0 ? _a : this.contextService.context.userConsentPreferences; }));
        });
    }
    getCurrentUserConsentCopy() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.pQueue.add(() => __awaiter(this, void 0, void 0, function* () { return this.contextService.context.userConsentCopy; }));
        });
    }
    getUserConsentPreferences(tokenInfo) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.pQueue.add(() => __awaiter(this, void 0, void 0, function* () {
                var _a;
                if (this.contextService.context.userConsentCookiePreferences) {
                    return this.contextService.context.userConsentCookiePreferences;
                }
                if (tokenInfo != null) {
                    Logger_1.default.debug("Fetching preferences for user", tokenInfo.userId);
                    if (tokenInfo.provider === Common_1.USER_IDENTITY_PROVIDER.O2) {
                        return (yield this.restService.doGetNamedUserConsent(tokenInfo)).data.preferences;
                    }
                    return UserService.userConsentCopyToPreferences(yield this.getUserConsentCopy()).map((element) => (Object.assign(Object.assign({}, element), { optIn: true })));
                }
                Logger_1.default.debug("Fetching preferences for unnamed user");
                return ((_a = this.localDataService.userConsentPreferences) !== null && _a !== void 0 ? _a : UserService.userConsentCopyToPreferences(yield this.getUserConsentCopy()));
            }));
        });
    }
    getCurrentUser() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.pQueue.add(() => __awaiter(this, void 0, void 0, function* () { return this.contextService.user; }));
        });
    }
    isDifferentUser(tokenInfo) {
        var _a, _b;
        const isNamedUser = tokenInfo != null;
        // Different user ID
        if (isNamedUser && tokenInfo.userId !== ((_a = this.contextService.context.user) === null || _a === void 0 ? void 0 : _a.user_id)) {
            return true;
        }
        // Load unnamed user; current user is not unnamed user
        if (!isNamedUser && ((_b = this.contextService.context.user) === null || _b === void 0 ? void 0 : _b.provider_name) !== Common_1.USER_IDENTITY_PROVIDER.SDK) {
            return true;
        }
        return false;
    }
    loadUser(tokenInfo) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.pQueue.add(() => __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c, _d;
                const isNamedUser = tokenInfo != null;
                if (this.isDifferentUser(tokenInfo)) {
                    (_a = this.onUserChanging) === null || _a === void 0 ? void 0 : _a.call(this, (_b = this.contextService.context.user) === null || _b === void 0 ? void 0 : _b.user_id, tokenInfo === null || tokenInfo === void 0 ? void 0 : tokenInfo.userId);
                }
                const userConsent = isNamedUser
                    ? yield this.setupNamedUser(tokenInfo)
                    : (_c = (yield this.setupLocalUnnamedUser())) !== null && _c !== void 0 ? _c : (yield this.setupRemoteUnnamedUser());
                /** @todo exact behavior is not well defined at moment. May want to move this assertion earlier in setup*User() so that the user context is not changed. */
                (0, superstruct_1.assert)(userConsent, UserConsentTypes_1.UserConsentStruct);
                this.contextService.context.tokenInfo = tokenInfo;
                this.reactToUserConsent(userConsent);
                return Object.assign(Object.assign({}, userConsent), { preferences: (_d = this.contextService.context.userConsentCookiePreferences) !== null && _d !== void 0 ? _d : userConsent.preferences });
            }));
        });
    }
    getUserConsentCopy() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.restService.doGetUnnamedUserConsent()).data.consentCopy;
        });
    }
    readConsentMetric() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.pQueue.add(() => __awaiter(this, void 0, void 0, function* () {
                var _a;
                const cookies = this.contextService.context.userConsentCookiePreferences
                    ? (_a = this.detectCookiePrivacySetting()) !== null && _a !== void 0 ? _a : this.contextService.context.userConsentCookiePreferences
                    : undefined;
                const preferences = cookies !== null && cookies !== void 0 ? cookies : this.contextService.readUserConsentPreferences();
                const { optIn, pendingUserPreferences } = UserService.isUserConsentOptIn(preferences);
                if (pendingUserPreferences) {
                    return new PendingUserActionError();
                }
                if (optIn) {
                    return preferences.map((p) => ({
                        consentId: p.consentId,
                        optIn: p.optIn,
                    }));
                }
                return new NoConsentError();
            }));
        });
    }
    saveUserConsentDetectToken(preferences) {
        return __awaiter(this, void 0, void 0, function* () {
            const tokenInfo = yield this.detectToken();
            Logger_1.default.debug("Token info", tokenInfo);
            return this.saveUserConsent(preferences, tokenInfo);
        });
    }
    saveUserConsent(preferences, tokenInfo) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.contextService.context.userConsentCookiePreferences) {
                Logger_1.default.error("Cookie preferences are in use", this.contextService.context.userConsentCookiePreferences);
                return;
            }
            if ((tokenInfo === null || tokenInfo === void 0 ? void 0 : tokenInfo.provider) === Common_1.USER_IDENTITY_PROVIDER.LDAP) {
                Logger_1.default.error("Not available for Azure token.");
                return;
            }
            yield this.pQueue.add(() => __awaiter(this, void 0, void 0, function* () {
                Logger_1.default.debug("Validate input User Consent", preferences);
                (0, superstruct_1.assert)(preferences, (0, superstruct_1.array)(UserConsentTypes_1.UserConsentPreferenceInputStruct));
                const localPreferences = this.contextService.readUserConsentPreferences();
                const inputPreferencesMap = new Map(preferences.map((item) => [item.consentId, item.optIn]));
                const localPreferencesMap = new Map(localPreferences === null || localPreferences === void 0 ? void 0 : localPreferences.map((item) => [item.consentId, item.optIn]));
                const redundant = Array.from(inputPreferencesMap.keys()).filter((key) => !localPreferencesMap.has(key));
                if (redundant.length) {
                    throw new Error(`Redundant preferences ${redundant}`);
                }
                const lacking = Array.from(localPreferencesMap.keys()).filter((key) => !inputPreferencesMap.has(key));
                if (lacking.length) {
                    throw new Error(`Lacking preferences ${lacking}`);
                }
                if ((0, isEqual_1.default)(inputPreferencesMap, localPreferencesMap)) {
                    Logger_1.default.debug("There is no change to update User Consent");
                    return;
                }
                let updatedPreferences;
                if (tokenInfo != null) {
                    Logger_1.default.debug("Call Rest API update User Consent", preferences);
                    const response = yield this.restService.doPostUserConsent(preferences, tokenInfo);
                    const lastUpdated = response.data;
                    updatedPreferences = preferences.map((p) => (Object.assign(Object.assign({}, p), { lastUpdated })));
                }
                else {
                    Logger_1.default.debug("Local unnamed update User Consent", preferences);
                    const lastUpdated = Date.now();
                    updatedPreferences = preferences.map((p) => (Object.assign(Object.assign({}, p), { lastUpdated })));
                    this.localDataService.userConsentPreferences = updatedPreferences;
                }
                this.contextService.writeUserConsentPreferences(updatedPreferences);
                Logger_1.default.debug("Send METRICS_SETTING for User Consent Change");
                this.contextService.sendEventInQueue("METRICS_SETTING", {
                    component: {
                        type: "metrics",
                        name: "optin_metrics",
                        attributes: { optin_status: UserService.isUserConsentOptIn(preferences).optIn ? "opt-in" : "opt-out" },
                    },
                }, preferences);
                this.contextService.dispatch("user_consent");
            }));
        });
    }
    setUserIntent(value) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.pQueue.add(() => {
                this.contextService.context.userIntent = value;
            });
        });
    }
    setCurrentUserConsentPreferences(preferences) {
        return __awaiter(this, void 0, void 0, function* () {
            const currentUser = yield this.getCurrentUser();
            // indicates user not yet loaded or set
            if (!currentUser) {
                Logger_1.default.error(Common_1.ERROR_MESSAGE.userConsentNotLoaded);
                return;
            }
            this.saveUserConsentDetectToken(preferences);
        });
    }
    detectCookiePrivacySetting(refCopy) {
        if (!(0, Utils_1.hasTealium)()) {
            return undefined;
        }
        const cookie = this.cookieService.privacyPreferences;
        if (!cookie) {
            return undefined;
        }
        const reference = refCopy !== null && refCopy !== void 0 ? refCopy : this.contextService.context.userConsentCopy;
        if (!reference) {
            Logger_1.default.error("No user consent copy is available for mapping");
            return undefined;
        }
        const lastUpdated = Date.now();
        const cookiePreferences = Object.fromEntries(cookie
            .split("|")
            .map((item) => item.split(":"))
            .filter((e) => e.length === 2 && e.every((item) => typeof item === "string")));
        return reference.copies.map(({ consentId }) => {
            const preference = {
                consentId,
                optIn: false,
                lastUpdated,
            };
            const alias = Common_1.PRIVACY_MAPPING.ID[consentId];
            if (alias) {
                preference.optIn = Common_1.PRIVACY_MAPPING.OPTIN[cookiePreferences[alias]] || false;
            }
            return preference;
        });
    }
    static userConsentCopyToPreferences(data) {
        return data.copies.map((copy) => ({
            consentId: copy.consentId,
            optIn: null,
            lastUpdated: null,
        }));
    }
    static isUserConsentOptIn(preferences) {
        if (preferences === null || preferences === void 0 ? void 0 : preferences.length) {
            let hasNull = false;
            let hasTrue = false;
            preferences.forEach((preference) => {
                hasNull = hasNull || preference.optIn == null;
                hasTrue = hasTrue || preference.optIn === true;
            });
            return {
                pendingUserPreferences: hasNull,
                optIn: !hasNull && hasTrue,
            };
        }
        // assume nullish preferences to be indication that user consent has not been loaded
        if (preferences == null) {
            return {
                optIn: false,
                pendingUserPreferences: true,
            };
        }
        return {
            optIn: false,
            pendingUserPreferences: false,
        };
    }
    static shouldShowUserConsentDialog(userConsent) {
        return userConsent.userActionRequired || userConsent.preferences.some((p) => p.optIn == null);
    }
}
exports.UserService = UserService;
